import React, { useContext, useEffect, useState } from 'react';

import {
  Box,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  IconButton,
  Button,
  Tooltip,
  Chip,
  Card,
  CardContent,
  Divider
} from '@mui/material';

import {
  ExpandMore,
  Delete,
  PictureAsPdf,
  Visibility,
  Edit,
  Add
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../auth/AuthContext';

import ProtectedAction from './ProtectedAction';

import { formatDate } from '../utils/dateFormatter';

import annoFiscaleApi from '../api/annoFiscaleApi';
import praticaApi from '../api/praticaApi';

const tipiDocumento = [
  'MULTA',
  'PAGOPA',
  'RATEIZZAZIONE_ADE',
  'ROTTAMAZIONE',
  'AMA',
  'ACCERTAMENTO_TARI',
  'F24',
  'FF24PREDETERMINATO',
  'TARI',
  'INTIMAZIONEPAG_AGRISC',
  'BOLLOAUTO',
  'INARCASSA',
  'FATTURA_PASSIVA',
  'FATTURA_ATTIVA',
  'ALTRO'
];

export default function DocumentoFascicoloTableMobile({
  documenti,
  contribuenteId,
  fascicoloId,
  onDownload,
  onDelete
}) {

  const { user } = useContext(AuthContext);

  const navigate = useNavigate();

  const [anniFiscali, setAnniFiscali] = useState([]);

  const [tabAnno, setTabAnno] = useState(0);

  useEffect(() => {

    if (!contribuenteId) {
      return;
    }

    fetchAnniFiscali();

  }, [contribuenteId]);

  const getPraticaStatusColor = (stato) => {

    switch (stato) {

      case 'APERTA':
        return 'info';

      case 'IN_ATTESA_DOCUMENTI':
        return 'warning';

      case 'COMPLETATA':
        return 'success';

      case 'CHIUSA':
        return 'error';

      default:
        return 'default';
    }
  };

  const fetchAnniFiscali = async () => {

    try {

      const res =
        await annoFiscaleApi.getByContribuente(contribuenteId);

      const anniConPratiche = await Promise.all(

        res.data.map(async (anno) => {

          const praticheRes =
            await praticaApi.getByAnnoFiscale(anno.id);

          return {
            ...anno,
            pratiche: praticheRes.data.content || []
          };
        })
      );

      setAnniFiscali(anniConPratiche);

    } catch (err) {

      console.error(err);
    }
  };

  const handleCreaScadenze = (doc) => {

    navigate(
      `/contribuenti/${contribuenteId}/documenti/upload`,
      {
        state: {
          documentoFascicoloId: doc.id
        }
      }
    );
  };

  const handleNuovoDocumento = (
    annoFiscaleId,
    praticaId = null
  ) => {

    navigate(
      `/fascicolo/${fascicoloId}/documento/create`,
      {
        state: {
          annoFiscaleId,
          praticaId,
          contribuenteId
        }
      }
    );
  };

  const handleNuovaPratica = (
    annoFiscaleId,
    anno
  ) => {

    navigate(
      `/fascicolo/${fascicoloId}/pratiche/create`,
      {
        state: {
          annoFiscaleId,
          anno
        }
      }
    );
  };

  const renderDocumentoCard = (d) => (

    <Card
      key={d.id}
      sx={{ mb: 2 }}
    >

      <CardContent>

        <Stack spacing={1}>

          <Typography fontWeight={700}>
            {d.tipo}
          </Typography>

          <Typography variant="body2">
            {d.descrizione}
          </Typography>

          <Typography variant="caption">
            Versione: {d.versione}
          </Typography>

          <Typography variant="caption">
            Upload: {formatDate(d.dataUpload)}
          </Typography>

          <Typography variant="caption">
            Caricato da: {d.caricatoDa}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
          >

            <ProtectedAction
              user={user}
              contractState={user.statoContratto}
            >

              <Tooltip title="Apri PDF">

                <IconButton
                  color="error"
                  onClick={() =>
                    onDownload(d.id, d.nomeFile)
                  }
                >
                  <PictureAsPdf />
                </IconButton>

              </Tooltip>

              <Tooltip title="Elimina">

                <IconButton
                  color="error"
                  onClick={() => onDelete(d.id)}
                >
                  <Delete />
                </IconButton>

              </Tooltip>

              {(
                (
                  d.parsedandloaded == null ||
                  d.parsedandloaded !== true
                )
                &&
                tipiDocumento.includes(d.tipo)
              ) && (

                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    handleCreaScadenze(d)
                  }
                >
                  Crea Scadenze
                </Button>
              )}

              {d.documentoId && (

                <Tooltip title="Visualizza Scadenze">

                  <IconButton
                    color="primary"
                    onClick={() =>
                      navigate(`/documenti/${d.documentoId}`)
                    }
                  >
                    <Visibility />
                  </IconButton>

                </Tooltip>
              )}

            </ProtectedAction>

          </Stack>

        </Stack>

      </CardContent>

    </Card>
  );

  const renderDocumenti = (docs) => {

    if (!docs || docs.length === 0) {

      return (
        <Typography color="text.secondary">
          Nessun documento presente
        </Typography>
      );
    }

    return docs.map(renderDocumentoCard);
  };

  const documentiSenzaPratica =
    documenti.filter(d => !d.praticaId);

  return (

    <Box>

      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
      >

        <Button
          fullWidth
          variant="contained"
          startIcon={<Add />}
          onClick={() =>
            navigate(`/fascicolo/${fascicoloId}/annofiscale/create`)
          }
        >
          Nuovo Anno Fiscale
        </Button>

      </Stack>

      <Tabs
        value={tabAnno}
        onChange={(e, value) => setTabAnno(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >

        {anniFiscali.map((anno) => (

          <Tab
            key={anno.id}
            label={anno.anno}
          />
        ))}

        <Tab label="Senza pratica" />

      </Tabs>

      {tabAnno === anniFiscali.length && (

        <Box>

          <Typography
            variant="h6"
            sx={{ mb: 2 }}
          >
            Documenti senza pratica
          </Typography>

          {renderDocumenti(documentiSenzaPratica)}

        </Box>
      )}

      {anniFiscali.map((anno, index) => {

        if (tabAnno !== index) {
          return null;
        }

        return (

          <Box key={anno.id}>

            <Stack spacing={2} sx={{ mb: 3 }}>

              <Chip
                label={`Anno fiscale ${anno.anno}`}
                color="primary"
              />

              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() =>
                  handleNuovaPratica(
                    anno.id,
                    anno.anno
                  )
                }
              >
                Nuova pratica
              </Button>

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() =>
                  handleNuovoDocumento(anno.id)
                }
              >
                Documento senza pratica
              </Button>

            </Stack>

            {anno.pratiche?.map((pratica) => {

              const docsPratica =
                documenti.filter(
                  d => d.praticaId === pratica.id
                );

              return (

                <Accordion
                  key={pratica.id}
                  sx={{ mb: 2 }}
                >

                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                  >

                    <Stack spacing={1} width="100%">

                      <Typography fontWeight={700}>
                        {pratica.nome}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                      >

                        <Chip
                          size="small"
                          label={`${docsPratica.length} documenti`}
                        />

                        <Chip
                          label={pratica.stato}
                          color={getPraticaStatusColor(pratica.stato)}
                        />

                        <IconButton
                          color="primary"
                          onClick={() =>
                            navigate(
                              `/fascicolo/pratiche/${pratica.id}/edit`
                            )
                          }
                        >
                          <Edit />
                        </IconButton>

                      </Stack>

                    </Stack>

                  </AccordionSummary>

                  <AccordionDetails>

                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Add />}
                      sx={{ mb: 2 }}
                      onClick={() =>
                        handleNuovoDocumento(
                          anno.id,
                          pratica.id
                        )
                      }
                    >
                      Carica Documento
                    </Button>

                    {renderDocumenti(docsPratica)}

                  </AccordionDetails>

                </Accordion>
              );
            })}

            {
              (!anno.pratiche ||
                anno.pratiche.length === 0) && (

                <Typography color="text.secondary">

                  Nessuna pratica presente

                </Typography>
              )
            }

          </Box>
        );
      })}

    </Box>
  );
}